import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IAbsencesState } from "./types";
import { IIDAStore } from "../types";
import { endOfDay, startOfDay } from "date-fns";
import { Absence } from "../../models/absences";
import { AbsenceType } from "../../models/absenceTypes-model";
import { absencesService } from "../../services/Absences.service";
import { configService } from "../../services/config.service";
import store from "../../store";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { IPeriode } from "../../utils/dateUtils";

// localStorage cache for absence types is only enabled under automation
// (Playwright sets navigator.webdriver = true). In a normal browser session
// the in-memory Vuex state is the only cache, so F5 refetches from the API.
const isAutomationRun = (): boolean =>
  typeof navigator !== "undefined" && navigator.webdriver === true;

const ABSENCE_TYPES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const absenceTypesCacheKey = (institutionId: number) =>
  `absenceTypes_${institutionId}`;

const loadCachedAbsenceTypes = (
  institutionId: number,
  salarySystem: number
): AbsenceType[] | null => {
  if (!isAutomationRun()) return null;
  try {
    const raw = localStorage.getItem(absenceTypesCacheKey(institutionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.salarySystem !== salarySystem) return null;
    if (Date.now() - parsed.savedAt > ABSENCE_TYPES_CACHE_TTL_MS) return null;
    return parsed.data as AbsenceType[];
  } catch {
    return null;
  }
};

const saveCachedAbsenceTypes = (
  institutionId: number,
  salarySystem: number,
  data: AbsenceType[]
): void => {
  if (!isAutomationRun()) return;
  try {
    localStorage.setItem(
      absenceTypesCacheKey(institutionId),
      JSON.stringify({ savedAt: Date.now(), salarySystem, data })
    );
  } catch {
    // localStorage may be full or disabled — silently ignore
  }
};

const clearCachedAbsenceTypes = (institutionId: number | null): void => {
  if (!isAutomationRun()) return;
  if (institutionId == null) return;
  try {
    localStorage.removeItem(absenceTypesCacheKey(institutionId));
  } catch {
    // ignore
  }
};

export const getInitialState = (): IAbsencesState => {
  return {
    searching: false,
    absences: new Map(),
    absencesKeys: {},
    absenceTypes: [],
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IAbsencesState, IIDAStore> = {
  GetAbsences: (state) => state.absences,
  GetAbsencesByEmployeeIds: (state, getters) => (employeeIds: number[]) => {
    const list: Absence[] = [];
    for (let index = 0; index < employeeIds.length; index++) {
      list.push(getters.GetAbsencesByEmployeeId(employeeIds[index]));
    }
    return list;
  },
  /* AbsenceType */
  GetAbsenceTypeById: (state) => (absenceTypeId: number) => {
    return state.absenceTypes.find((a) => a.id === absenceTypeId);
  },
  GetAbsenceTypes: (state) => state.absenceTypes,
  GetAbsencesByEmployeeId: (state) => (employeeId: number) => {
    if (state.absencesKeys[employeeId]) {
      return state.absencesKeys[employeeId];
    } else {
      return [];
    }
  },
  GetAbsencesByEmployeeIdAndDate: (state, getters) => (employeeId: number, dato: Date) => {
    const absences: Absence[] = getters.GetAbsencesByEmployeeId(employeeId);
    const list: Absence[] = [];
    for (let index = 0; index < absences.length; index++) {
      const absence = absences[index];
      if (absence.isAbsenceActiveOnDate(dato)) {
        list.push(absence);
      }
    }
    return list;
  },

  GetAbsencesByDate:
    (state) =>
      (date: Date): Absence[] => {
        return Object.values(state.absences)?.filter((ab) => ab.isAbsenceActiveOnDate(date)) || [];
      }
};

export const actions: ActionTree<IAbsencesState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchAbsences(
    { commit, state },
    query: { from: Date; to: Date; forceUpdate: boolean }
  ): Promise<Absence[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const periode = { fromDate: from, toDate: to } as IPeriode;
      if (query.forceUpdate || !state.storeCache.isCached(periode)) {
        try {
          absencesService.fetchAbsences(from, to).then((absences) => {
            commit("updateAbsences", { absences, from, to });
            return resolve(absences);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const absences: Absence[] = Object.values(state.absences).filter(
          (ab) => (ab.end_date >= from || ab.is_open) && ab.start_date < to
        );
        return resolve(absences);
      }
    });
  },
  fetchAbsenceById({ commit, state }, id: number): Promise<Absence | undefined> {
    return new Promise((resolve) => {
      const absence: Absence | undefined = Object.values(state.absences).find((a) => a.id === id);
      if (!absence) {
        absencesService.fetchAbsenceById(id).then((absence) => {
          if (absence) {
            commit("updateAbsences", {
              absences: [absence],
              from: absence.start_date,
              to: absence.end_date
            });
          }
          resolve(absence);
        });
      } else {
        resolve(absence);
      }
    });
  },

  fetchAbsenceTypes() {
    return new Promise((resolve, reject) => {
      store
        .dispatch("absences/fetchAllAbsenceTypes")
        .then((allAbsenceTypes: AbsenceType[]) => {
          resolve(allAbsenceTypes);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchAllAbsenceTypes({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (state.absenceTypes.length === 0) {
        const institutionId = configService.institutionId();
        const salerySystem: number = store.getters["institutions/getSalerySystem"]();

        // Under Playwright, try localStorage first to keep tests fast.
        // In a normal browser this is a no-op (see isAutomationRun above).
        if (institutionId != null) {
          const cached = loadCachedAbsenceTypes(institutionId, salerySystem);
          if (cached && cached.length > 0) {
            commit("updateAbsenceTypes", cached);
            resolve(cached);
            return;
          }
        }

        try {
          absencesService.fetchAbsenceTypes().then((absenceTypes) => {
            const nonePlannedCodes = AbsenceType.nonePlannedCodesFor(salerySystem);
            absenceTypes.forEach(
              (at) => (at.nonePlanned = nonePlannedCodes.includes(at.code))
            );
            commit("updateAbsenceTypes", absenceTypes);
            if (institutionId != null) {
              saveCachedAbsenceTypes(institutionId, salerySystem, absenceTypes);
            }
            resolve(absenceTypes);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.absenceTypes);
      }
    });
  },
  createAbsence({ commit }, absence: Absence) {
    return new Promise((resolve, reject) => {
      absencesService
        .createAbsence(absence)
        .then((absence: Absence) => {
          commit("updateAbsences", {
            absences: [absence],
            from: absence.start_date,
            to: absence.Periode.toDate
          });
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateAbsence({ commit }, absence: Absence) {
    return new Promise((resolve, reject) => {
      absencesService
        .updateAbsence(absence)
        .then((absence: Absence) => {
          commit("updateAbsences", { absences: [absence], from: new Date(), to: new Date() });
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteAbsence({ commit }, absenceId: number) {
    return new Promise((resolve, reject) => {
      absencesService
        .deleteAbsence(absenceId)
        .then((absence: any) => {
          commit("removeAbsence", absenceId);
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createAbsenceType({ commit }, absenceType: AbsenceType) {
    return new Promise((resolve, reject) => {
      absencesService
        .createAbsenceType(absenceType)
        .then((absenceType: AbsenceType) => {
          commit("insertAbsenceType", absenceType);
          clearCachedAbsenceTypes(configService.institutionId());
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateAbsenceType({ commit }, absenceType: AbsenceType) {
    return new Promise((resolve, reject) => {
      absencesService
        .updateAbsenceType(absenceType)
        .then((absenceType: AbsenceType) => {
          commit("updateAbsenceType", absenceType);
          clearCachedAbsenceTypes(configService.institutionId());
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteAbsenceType({ commit }, absenceTypeId: number) {
    return new Promise((resolve, reject) => {
      absencesService
        .deleteAbsenceType(absenceTypeId)
        .then((absenceType: any) => {
          commit("removeAbsenceType", absenceTypeId);
          clearCachedAbsenceTypes(configService.institutionId());
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IAbsencesState> = {
  reset(state: IAbsencesState) {
    Object.assign(state, getInitialState());
  },
  removeAbsence(state: IAbsencesState, absenceId: number) {
    const oldAbsence = state.absences[absenceId];

    if (oldAbsence) {
      state.absencesKeys[oldAbsence.employee_id] = state.absencesKeys[
        oldAbsence.employee_id
      ].filter((a) => a.id !== oldAbsence.id);
    }
    delete state.absences[absenceId];
  },
  insertAbsenceType(state: IAbsencesState, absenceType: AbsenceType) {
    state.absenceTypes.push(absenceType);
  },
  removeAbsenceType(state: IAbsencesState, absenceTypeId: number) {
    const index = state.absenceTypes.findIndex((a) => a.id === absenceTypeId);
    state.absenceTypes.splice(index, 1);
  },
  updateAbsenceType(state: IAbsencesState, absenceType: AbsenceType) {
    const index = state.absenceTypes.findIndex((a) => a.id === absenceType.id);
    state.absenceTypes[index] = absenceType;
  },
  updateAbsences(state: IAbsencesState, payload: { absences: Absence[]; from: Date; to: Date }) {
    for (let index = 0; index < payload.absences.length; index++) {
      const absence: Absence = payload.absences[index];
      state.absences[absence.id] = absence;
      const key = absence.employee_id;
      let employment = state.absencesKeys[key];
      if (!employment) {
        employment = state.absencesKeys[key] = [];
      }
      const ix = employment.findIndex((d) => d.equals(absence));
      if (ix > -1) {
        employment[ix] = absence;
      } else {
        employment.push(absence);
      }
    }

    if (payload.from && payload.to) {
      state.storeCache.addCache(new storeCachePeriode({ fromDate: payload.from, toDate: payload.to }));
    }
  },
  updateAbsenceTypes(state: IAbsencesState, absenceTypes: AbsenceType[]) {
    state.absenceTypes = [
      ...state.absenceTypes.filter(
        (oldAbsenceTypes) => !absenceTypes.find((newAT) => newAT.equals(oldAbsenceTypes))
      ),
      ...absenceTypes
    ];
  }
};

export const absencesStoreModule: Module<IAbsencesState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
